// Драфт выбранного блока живёт ОДИН на редактор: и правая панель, и превью-телефон
// читают его отсюда. Без этого WYSIWYG невозможен — превью рисовало бы сохранённое
// с сервера, а правки были бы видны только после нажатия «Сохранить».
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAppSelector } from '@/store/hooks'
import { useBlockDraft, type BlockDraftResult } from './useBlockDraft'

export interface BlockDraftContextValue extends BlockDraftResult {
  /** id выделенного элемента дизайн-блока (в остальных типах не используется). */
  selectedElementId: string | null
  setSelectedElementId: (id: string | null) => void
}

const FALLBACK: BlockDraftContextValue = {
  block: null,
  draft: null,
  setDraft: () => {},
  isDirty: false,
  isSaving: false,
  hasRunConflict: false,
  takeServerVersion: () => {},
  canUndo: false,
  canRedo: false,
  undo: () => {},
  redo: () => {},
  doSave: async () => {},
  doDelete: async () => false,
  selectedElementId: null,
  setSelectedElementId: () => {},
}

const BlockDraftContext = createContext<BlockDraftContextValue>(FALLBACK)

/**
 * Узкая проекция того же черновика — «какой блок открыт и есть ли в нём
 * несохранённая правка». Заведена отдельным контекстом (M-5): гейты старта
 * (`useUnsavedBlockGate`) читают ТОЛЬКО эти два значения, а полное значение
 * контекста меняется на КАЖДОЕ нажатие клавиши в блоке — вместе с ним
 * перерисовывалась бы вся AI-панель с лентой сообщений. Мемоизировать полное
 * значение бесполезно: `draft` — его часть, оно обязано меняться.
 */
export interface BlockDraftGate {
  blockId: number | null
  /** Урок открытого блока. Месту загрузки картинки (п.24) нужна ПАРА id, чтобы
   *  назвать владельца фоновой задачи, а редакторы блока про стор не знают
   *  вовсе — эта проекция их единственный источник «кто я». Частоту обновления
   *  проекции урок не меняет: он меняется реже блока. */
  lessonId: number | null
  isDirty: boolean
}

const BlockDraftGateContext = createContext<BlockDraftGate>({
  blockId: null,
  lessonId: null,
  isDirty: false,
})

/**
 * Вторая узкая проекция того же черновика — шаги истории правок (п.11) для
 * кнопок Undo/Redo в шапке. Заведена соседкой `BlockDraftGate`, а не полем в
 * ней: гейты старта платных прогонов не должны перерисовываться от того, что
 * у автора появился шаг назад, а кнопкам шапки нечего знать про `isDirty`.
 * Функции стабильны (`useCallback` в `useDraftHistory`), поэтому значение
 * меняется только на смене доступности шагов.
 */
export interface BlockHistory {
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void
}

const NO_HISTORY: BlockHistory = {
  canUndo: false,
  canRedo: false,
  undo: () => {},
  redo: () => {},
}

const BlockHistoryContext = createContext<BlockHistory>(NO_HISTORY)

// eslint-disable-next-line react-refresh/only-export-components
export const useBlockDraftContext = (): BlockDraftContextValue => useContext(BlockDraftContext)

// eslint-disable-next-line react-refresh/only-export-components
export const useBlockDraftGate = (): BlockDraftGate => useContext(BlockDraftGateContext)

// eslint-disable-next-line react-refresh/only-export-components
export const useBlockHistory = (): BlockHistory => useContext(BlockHistoryContext)

interface BlockDraftProviderProps {
  children: ReactNode
  /**
   * §23: пробрасывается в `useBlockDraft` ради инвалидации `Course` на
   * удаление блока — обязателен (без него бейдж плашки урока не обновится).
   */
  courseId: number
}

export const BlockDraftProvider = ({ children, courseId }: BlockDraftProviderProps) => {
  const draftResult = useBlockDraft(courseId)
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const draftId = draftResult.draft?.id ?? null

  useEffect(() => {
    setSelectedElementId(null)
  }, [draftId])

  const blockId = draftResult.block?.id ?? null
  const lessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const { isDirty, canUndo, canRedo, undo, redo } = draftResult
  const gate = useMemo(() => ({ blockId, lessonId, isDirty }), [blockId, lessonId, isDirty])
  const history = useMemo(() => ({ canUndo, canRedo, undo, redo }), [canUndo, canRedo, undo, redo])

  return (
    <BlockDraftContext.Provider value={{ ...draftResult, selectedElementId, setSelectedElementId }}>
      <BlockDraftGateContext.Provider value={gate}>
        <BlockHistoryContext.Provider value={history}>{children}</BlockHistoryContext.Provider>
      </BlockDraftGateContext.Provider>
    </BlockDraftContext.Provider>
  )
}
